import { Plus, Trash } from "@medusajs/icons"
import { Button, IconButton, Input, Select, Text, toast } from "@medusajs/ui"
import { useEffect, useState } from "react"
import { Field } from "@/components/form"
import { ErrorState, SaveBar, Section } from "@/components/ui"
import { FIELD_TYPES, useCustomData, useSaveCustomData, type Definition, type FieldType } from "@/lib/custom-data"
import { useCan } from "@/lib/permissions"
import { errorMessage } from "@/lib/sdk"
import { SettingsTitle } from "./settings-general"

const newId = () => Array.from(crypto.getRandomValues(new Uint8Array(8)), (b) => "abcdefghijklmnopqrstuvwxyz0123456789"[b % 36]).join("")
const slug = (v: string) => v.toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_+|_+$/g, "").replace(/^[0-9]+/, "").slice(0, 40)

/** Settings → Custom data: metafield definitions for products. */
export function CustomDataSettingsPage() {
  const { data, error } = useCustomData()
  const save = useSaveCustomData()
  const writable = useCan()("settings:write")
  const [list, setList] = useState<Definition[] | null>(null)
  useEffect(() => { if (data) setList(data.definitions) }, [data])
  if (error) return <ErrorState error={error} />
  if (!list || !data) return <Text size="small">Loading…</Text>
  const dirty = JSON.stringify(list) !== JSON.stringify(data.definitions)
  const dup = new Set(list.map((d) => d.key)).size !== list.length
  const bad = dup || list.some((d) => !d.name.trim() || !/^[a-z][a-z0-9_]*$/.test(d.key))
  const put = (id: string, p: Partial<Definition>) => setList(list.map((d) => (d.id === id ? { ...d, ...p } : d)))
  return (
    <div className="flex flex-col gap-4 pb-24">
      <SettingsTitle title="Metafields and metaobjects" subtitle="Custom fields that appear on the product page in admin. Values are stored on the product and delivered to the storefront as metadata." />
      <Section title="Product metafields">
        <div className="flex flex-col gap-3 p-4">
          {!list.length && <Text size="small" className="text-ui-fg-subtle">No custom fields yet. Add one, for example “Ingredients” or “Care instructions”.</Text>}
          {list.map((d) => (
            <div key={d.id} className="flex items-start gap-2 rounded-lg border border-ui-border-base p-3">
              <div className="grid min-w-0 flex-1 gap-3 sm:grid-cols-2">
                <Field label="Name"><Input value={d.name} maxLength={60} disabled={!writable} onChange={(e) => { const name = e.target.value; put(d.id, { name, key: d.key === slug(d.name) || !d.key ? slug(name) : d.key }) }} /></Field>
                <Field label="Key" hint={`Stored as custom.${d.key || "key"}`}><Input value={d.key} maxLength={40} disabled={!writable} onChange={(e) => put(d.id, { key: e.target.value.toLowerCase() })} aria-invalid={!!d.key && !/^[a-z][a-z0-9_]*$/.test(d.key)} /></Field>
                <Field label="Type">
                  <Select value={d.type} disabled={!writable} onValueChange={(v) => put(d.id, { type: v as FieldType })}>
                    <Select.Trigger aria-label="Type"><Select.Value /></Select.Trigger>
                    <Select.Content>{(Object.keys(FIELD_TYPES) as FieldType[]).map((t) => <Select.Item key={t} value={t}>{FIELD_TYPES[t]}</Select.Item>)}</Select.Content>
                  </Select>
                </Field>
                <Field label="Description (optional)"><Input value={d.description} maxLength={200} disabled={!writable} onChange={(e) => put(d.id, { description: e.target.value })} /></Field>
              </div>
              {writable && <IconButton size="small" variant="transparent" aria-label="Remove field" onClick={() => setList(list.filter((x) => x.id !== d.id))}><Trash /></IconButton>}
            </div>
          ))}
          {dup && <Text size="small" className="text-ui-fg-error">Each key must be unique.</Text>}
          {writable && list.length < 50 && (
            <div><Button type="button" size="small" variant="secondary" onClick={() => setList([...list, { id: newId(), owner: "product", name: "", key: "", type: "text", description: "" }])}><Plus />Add definition</Button></div>
          )}
          <Text size="xsmall" className="text-ui-fg-subtle">Removing a definition hides the field in the editor; existing values stay on the products.</Text>
        </div>
      </Section>
      <SaveBar visible={writable && dirty} message="Unsaved changes" onDiscard={() => setList(data.definitions)} disabled={bad} saving={save.isPending}
        onSave={() => save.mutate(list, { onSuccess: () => toast.success("Custom data saved"), onError: (e) => toast.error(errorMessage(e)) })} />
    </div>
  )
}
